/**
 * Ersatz für die überschriebene Methode `SetMatchcode()` der Basisdialoge
 * (`IwmDialogDokumentUpload` / `IwmDialogDokumentList`): Der Titelzusatz wird beim Öffnen
 * nachgeladen. React kennt keine Vererbung — die abgeleiteten Dialoge liefern den Zusatz
 * deshalb als Prop und benutzen dafür die Haken dieser Datei.
 */

import { useCallback, useEffect, useState } from 'react';

import { containerApi } from '@/api/services/containerApi';
import { receiptApi } from '@/api/services/receiptApi';
import type { Container, Receipt } from '@/types/entities';

/** `String.IsNullOrEmpty(Matchcode) ? caption : $"{caption} - {Matchcode}"`. */
export function withMatchcode(caption: string, matchcode: string): string {
  return matchcode === '' ? caption : `${caption} - ${matchcode}`;
}

/**
 * Lädt den Titelzusatz, sobald der Dialog geöffnet wird, und leert ihn beim Schließen
 * (der Altclient setzt `Matchcode = ""` in `Show()`).
 *
 * `load` muss stabil sein (`useCallback`), sonst wird bei jedem Rendern neu geladen.
 */
export function useMatchcode(open: boolean, load: () => Promise<string>): string {
  const [matchcode, setMatchcode] = useState('');

  useEffect(() => {
    if (!open) {
      setMatchcode('');
      return;
    }
    let active = true;
    load()
      .then((value) => {
        if (active) setMatchcode(value);
      })
      // Der Altclient lässt den Zusatz leer, wenn der Datensatz nicht geladen werden kann.
      .catch(() => {
        if (active) setMatchcode('');
      });
    return () => {
      active = false;
    };
  }, [load, open]);

  return matchcode;
}

/** `Container {ContainerNo}` — Port von `IwmDialogContainerDokument*.SetMatchcode`. */
export function useContainerMatchcode(open: boolean, viewId: number, containerId: number): string {
  const load = useCallback(async () => {
    if (containerId <= 0) return '';
    // `APIViewBase.GetEntry` liefert bei leerer Antwort null; der Altclient prüft darauf.
    const container: Container | null = await containerApi.getEntry(viewId, containerId);
    return container === null ? '' : `Container ${container.ContainerNo ?? ''}`;
  }, [containerId, viewId]);

  return useMatchcode(open, load);
}

/** `Receipt {Rechnungsnummer}` — Port von `IwmDialogRechnungDokument*.SetMatchcode`. */
export function useReceiptMatchcode(open: boolean, viewId: number, receiptId: number): string {
  const load = useCallback(async () => {
    if (receiptId <= 0) return '';
    const receipt: Receipt | null = await receiptApi.getEntry(viewId, receiptId);
    return receipt === null ? '' : `Receipt ${receipt.Rechnungsnummer ?? ''}`;
  }, [receiptId, viewId]);

  return useMatchcode(open, load);
}
